import { PressableScale } from "pressto";
import { View, Text } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Icon } from "@src/components/Icon";
import { FC, memo } from "react";
import Animated, { FadeIn, FadeOut } from "react-native-reanimated";

interface WeekNavigationBarProps {
  label: string;
  canGoPrevious: boolean;
  canGoNext: boolean;
  isCurrentWeek: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onGoToToday: () => void;
}

export const WeekNavigationBar: FC<WeekNavigationBarProps> = memo(
  ({ label, canGoPrevious, canGoNext, isCurrentWeek, onPrevious, onNext, onGoToToday }) => {
    const { theme } = useUnistyles();

    return (
      <View style={styles.container}>
        <View style={styles.navRow}>
          <View style={styles.leftControls}>
            <PressableScale
              onPress={onPrevious}
              enabled={canGoPrevious}
              style={[styles.iconButton, { opacity: canGoPrevious ? 1 : 0.3 }]}
            >
              <Icon name="chevronLeft" size={24} color={theme.colors.textMuted as string} />
            </PressableScale>
          </View>
          <View style={styles.labelContainer}>
            <Text textStyle="xs/semibold" color="text" textAlign="center">
              {label}
            </Text>
          </View>
          <View style={styles.rightControls}>
            <PressableScale
              onPress={onNext}
              enabled={canGoNext}
              style={[styles.iconButton, { opacity: canGoNext ? 1 : 0.3 }]}
            >
              <Icon name="chevronRight" size={24} color={theme.colors.textMuted as string} />
            </PressableScale>
          </View>
        </View>
        {!isCurrentWeek && (
          <Animated.View entering={FadeIn} exiting={FadeOut}>
            <PressableScale onPress={onGoToToday}>
              <View style={styles.todayLabel}>
                <Text textStyle="xs/regular" color="text">
                  Today
                </Text>
              </View>
            </PressableScale>
          </Animated.View>
        )}
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingTop: 0,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing.md,
  },
  navRow: {
    flexDirection: "row",
    alignItems: "center",
    height: 30,
  },
  leftControls: {
    flexDirection: "row",
    alignItems: "center",
  },
  labelContainer: {
    width: 140,
    alignItems: "center",
    justifyContent: "center",
  },
  rightControls: {
    flexDirection: "row",
    alignItems: "center",
  },
  iconButton: {
    width: 32,
    height: 32,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  todayLabel: {
    borderWidth: 1.5,
    borderRadius: theme.spacing.xl,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    backgroundColor: theme.colors.interactive.secondary.default_background,
    borderColor: theme.colors.border,
    height: 30,
    minWidth: 60,
    justifyContent: "center",
    alignItems: "center",
  },
}));
